/**
 * Phase 107 (Plan 107-10 / SLA-PRICE-01, SLA-PRICE-02, SLA-PRICE-08) — the
 * SLA Pricing Configurator editor: page shell, dirty tracking, live price
 * preview over unsaved paint state, save flow, reset and preset.
 *
 * Layout reference only: `git show bec322bc:frontend/src/pages/projects/CoveragePage.tsx`
 * was project-scoped and every data hook in it is dead against current HEAD;
 * only its header/toolbar/grid-plus-sidebar/sticky-bottom-bar shape carries
 * over. All wiring here is rebuilt against `useSlaPricing.ts` (Plan 107-07).
 *
 * Needs neither a lead nor a project in context (D-01, D-11) — the only
 * route param is the configuration id. MKS-346 adds a read-only "Linked
 * leads" panel in the sidebar (behind its release flag): the configuration
 * still cannot be linked or unlinked from here, it only names the deals it
 * was priced for, so the route param stays the only input.
 *
 * Two persistence granularities, never conflated (107-UI-SPEC.md
 * Implementation Note 1): band CRUD is immediate-save (each mutation fires
 * on its own submit/blur); cell painting is batch — it accumulates in local
 * component state and is submitted as one `useSaveCells` call when Save is
 * clicked. There is no background-save-on-paint and no per-drag mutation: with D-10's named
 * side-by-side variants, an accidental background save that clobbers a
 * saved variant mid-experiment would be actively harmful, so that path is
 * deliberately not carried forward from the deleted grid.
 *
 * Save flow (D-09, SC-2): the Save button is never hard-disabled on
 * validity — only while a save is in flight. A rate-less band referenced by
 * painted hours is caught client-side before the request fires, naming the
 * offending band(s); if the request does fire and the server refuses it
 * instead, that message is passed through verbatim. Both the refusal and a
 * successful save render inside the sticky Save bar, next to the button, so
 * the outcome can never scroll out of view. This is the whole point: the
 * refusal is proven by test, not by disabled UI.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PricingGrid, type CellCoord } from '../../components/sla-pricing/PricingGrid';
import { BandPanel } from '../../components/sla-pricing/BandPanel';
import { PriceSummaryCard } from '../../components/sla-pricing/PriceSummaryCard';
import { TimezonePicker } from '../../components/sla-pricing/TimezonePicker';
import { ResetGridConfirmModal } from '../../components/sla-pricing/ResetGridConfirmModal';
import {
  ApplyPresetConfirmModal,
  PRESET_MON_FRI_0800_1800,
} from '../../components/sla-pricing/ApplyPresetConfirmModal';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import {
  useConfiguration,
  useUpdateConfiguration,
  useCreateBand,
  useUpdateBand,
  useDeleteBand,
  useSaveCells,
} from '../../hooks/useSlaPricing';
import { computeMonthlyPriceClient, computeBandBreakdownClient } from '../../components/sla-pricing/priceMath';
import type { SlaPricingCell } from '../../services/api/slaPricing';
import { toErrorMessage } from '../../utils/toErrorMessage';

const SAVE_BLOCKED_MESSAGE =
  "Can't save — every band needs an hourly rate before this configuration can be saved.";
const UNSAVED_PAINTING_CONFIRM_TEXT = 'You have unsaved painting. Leave anyway?';

interface InvalidBandRef {
  id: number;
  name: string;
}

type SaveStatus = { kind: 'error' | 'success'; message: string } | null;

function cellKey(dayOfWeek: number, hour: number): string {
  return `${dayOfWeek}-${hour}`;
}

function formatSaveRefusal(invalidBands: InvalidBandRef[]): string {
  if (invalidBands.length === 1) {
    return `Can't save — "${invalidBands[0]!.name}" has no hourly rate. Set it in Price bands, then Save again.`;
  }
  const list = invalidBands.map((b) => `"${b.name}"`).join(', ');
  return `Can't save — ${invalidBands.length} bands have no hourly rate: ${list}. Set them in Price bands, then Save again.`;
}

function formatSaveSuccess(paintedCount: number): string {
  if (paintedCount === 0) return 'Saved — the grid is now empty.';
  if (paintedCount === 1) return 'Saved — 1 painted hour.';
  return `Saved — ${paintedCount} painted hours.`;
}

export default function SlaPricingEditorPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);

  const { data, isLoading } = useConfiguration(id);
  const configuration = data?.data;

  const updateConfiguration = useUpdateConfiguration();
  const createBand = useCreateBand();
  const updateBand = useUpdateBand();
  const deleteBand = useDeleteBand();
  const saveCells = useSaveCells();

  // Cell painting is batch state (see file header): a Map keyed by
  // "{dayOfWeek}-{hour}" -> bandId, seeded once from the loaded
  // configuration's saved cells. `null` means "not yet seeded".
  const [localCells, setLocalCells] = useState<Map<string, number> | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [armedBandId, setArmedBandId] = useState<number | null>(null);
  const [isErasing, setIsErasing] = useState(false);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  useEffect(() => {
    if (configuration && localCells === null) {
      const seeded = new Map<string, number>();
      for (const cell of configuration.cells) {
        seeded.set(cellKey(cell.dayOfWeek, cell.hour), cell.bandId);
      }
      setLocalCells(seeded);
    }
  }, [configuration, localCells]);

  const bands = useMemo(() => configuration?.bands ?? [], [configuration]);

  // Clear the armed band if it no longer exists (e.g. it was just deleted).
  useEffect(() => {
    if (armedBandId !== null && !bands.some((b) => b.id === armedBandId)) {
      setArmedBandId(null);
    }
  }, [armedBandId, bands]);

  const cellsMap = useMemo(() => localCells ?? new Map<string, number>(), [localCells]);

  const paintedCells: SlaPricingCell[] = useMemo(() => {
    const list: SlaPricingCell[] = [];
    for (const [key, bandId] of cellsMap) {
      const parts = key.split('-');
      const dayOfWeek = Number(parts[0]);
      const hour = Number(parts[1]);
      list.push({ dayOfWeek, hour, bandId });
    }
    return list;
  }, [cellsMap]);

  const hoursPerBand = useMemo(() => {
    const map: Record<number, number> = {};
    for (const cell of paintedCells) {
      map[cell.bandId] = (map[cell.bandId] ?? 0) + 1;
    }
    return map;
  }, [paintedCells]);

  // D-09: bands referenced by painted hours whose rate is null — shown
  // continuously in PriceSummaryCard's proactive warning, independent of
  // whether Save has been attempted yet.
  const invalidBands = useMemo(() => {
    const usedBandIds = new Set(paintedCells.map((c) => c.bandId));
    return bands
      .filter((b) => usedBandIds.has(b.id) && b.hourlyRate === null)
      .map((b) => ({ id: b.id, name: b.name }));
  }, [paintedCells, bands]);

  // Live preview over the local, unsaved cell map — computeMonthlyPriceClient
  // and computeBandBreakdownClient are pinned to the same fixtures as the
  // backend's Decimal implementation, so the preview and the saved quote
  // cannot drift.
  const breakdown = useMemo(
    () => computeBandBreakdownClient(bands, paintedCells),
    [bands, paintedCells],
  );
  const monthlyTotal = useMemo(
    () => computeMonthlyPriceClient(bands, paintedCells),
    [bands, paintedCells],
  );

  // Guard actual tab close / reload. Modern browsers ignore custom
  // beforeunload text, so the Copywriting Contract's exact wording is only
  // shown for in-app navigation via the breadcrumb link below.
  useEffect(() => {
    function handleBeforeUnload(e: { preventDefault: () => void; returnValue: unknown }) {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    }
    const listener = handleBeforeUnload as unknown as EventListener;
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, [isDirty]);

  function handleBreadcrumbClick(e: React.MouseEvent) {
    if (isDirty && !window.confirm(UNSAVED_PAINTING_CONFIRM_TEXT)) {
      e.preventDefault();
    }
  }

  // Clears saveStatus alongside every dirtying action, so a stale success
  // or refusal never sits beside grid state it no longer describes.
  function markDirty() {
    setIsDirty(true);
    setSaveStatus(null);
  }

  function handlePaintRange(coords: CellCoord[]) {
    setLocalCells((prev) => {
      const next = new Map(prev ?? []);
      if (isErasing) {
        for (const c of coords) next.delete(cellKey(c.dayOfWeek, c.hour));
      } else if (armedBandId !== null) {
        for (const c of coords) next.set(cellKey(c.dayOfWeek, c.hour), armedBandId);
      }
      return next;
    });
    markDirty();
  }

  function applyPresetWith(bandId: number) {
    // Same per-coordinate assignment semantics onPaintRange uses (band id
    // per coordinate), but as a full replacement rather than an additive
    // paint — the preset also clears every hour outside its own window
    // (D-16), which an additive paint through onPaintRange alone cannot do.
    const next = new Map<string, number>();
    for (const c of PRESET_MON_FRI_0800_1800) {
      next.set(cellKey(c.dayOfWeek, c.hour), bandId);
    }
    setLocalCells(next);
    markDirty();
  }

  const bandsEmpty = bands.length === 0;
  const gridEmpty = cellsMap.size === 0;

  const resetDisabled = bandsEmpty;
  const resetTitle = bandsEmpty
    ? 'Add a band first.'
    : gridEmpty
      ? 'Grid is already empty.'
      : undefined;

  const presetDisabled = bandsEmpty || armedBandId === null;
  const presetTitle = bandsEmpty
    ? 'Add a band first.'
    : armedBandId === null
      ? 'Arm a band first.'
      : undefined;

  function handleResetClick() {
    if (bandsEmpty || gridEmpty) return;
    setIsResetModalOpen(true);
  }

  function confirmReset() {
    setLocalCells(new Map());
    markDirty();
    setIsResetModalOpen(false);
  }

  function handleApplyPresetClick() {
    if (bandsEmpty || armedBandId === null) return;
    if (gridEmpty) {
      applyPresetWith(armedBandId);
    } else {
      setIsPresetModalOpen(true);
    }
  }

  function confirmApplyPreset() {
    if (armedBandId !== null) applyPresetWith(armedBandId);
    setIsPresetModalOpen(false);
  }

  function handleSave() {
    if (!configuration) return;
    setSaveStatus(null);
    if (invalidBands.length > 0) {
      setSaveStatus({ kind: 'error', message: formatSaveRefusal(invalidBands) });
      return;
    }
    const submittedCount = paintedCells.length;
    saveCells.mutate(
      { configurationId: configuration.id, cells: paintedCells },
      {
        onSuccess: () => {
          setIsDirty(false);
          setSaveStatus({ kind: 'success', message: formatSaveSuccess(submittedCount) });
        },
        onError: (err) => {
          setSaveStatus({ kind: 'error', message: toErrorMessage(err, SAVE_BLOCKED_MESSAGE) });
        },
      },
    );
  }

  function handleAddBand(input: {
    name: string;
    abbreviation: string;
    color: string;
    hourlyRate: string | null;
  }) {
    if (!configuration) return;
    createBand.mutate({ configurationId: configuration.id, input });
  }

  function handleUpdateRate(bandId: number, hourlyRate: string | null) {
    if (!configuration) return;
    updateBand.mutate({ configurationId: configuration.id, bandId, input: { hourlyRate } });
  }

  function handleDeleteBand(bandId: number) {
    if (!configuration) return;
    deleteBand.mutate(
      { configurationId: configuration.id, bandId },
      {
        onSuccess: () => {
          // The backend cascades the delete to that band's cells. Purge any
          // matching local (possibly still-unsaved) entries so the grid
          // never renders a stale reference to a band that no longer exists.
          setLocalCells((prev) => {
            if (!prev) return prev;
            const next = new Map(prev);
            for (const [key, id] of next) {
              if (id === bandId) next.delete(key);
            }
            return next;
          });
        },
      },
    );
  }

  function startEditName() {
    if (!configuration) return;
    setNameDraft(configuration.name);
    setIsEditingName(true);
  }

  function commitName() {
    if (!configuration) return;
    const trimmed = nameDraft.trim();
    setIsEditingName(false);
    if (trimmed === '' || trimmed === configuration.name) return;
    updateConfiguration.mutate({ id: configuration.id, input: { name: trimmed } });
  }

  if (isLoading || !configuration) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <header className="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Link
            to="/sales/sla-pricing"
            onClick={handleBreadcrumbClick}
            className="text-[13px] text-gray-500 hover:underline"
          >
            SLA Pricing
          </Link>
          <span className="text-gray-300">/</span>
          {isEditingName ? (
            <input
              type="text"
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitName();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setIsEditingName(false);
                }
              }}
              className="text-2xl font-bold text-gray-900 border-b border-primary-500 focus:outline-none"
            />
          ) : (
            <h1
              onClick={startEditName}
              className="text-2xl font-bold text-gray-900 truncate cursor-text"
              title="Click to rename"
            >
              {configuration.name}
            </h1>
          )}
          <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
            {configuration.currency}
          </span>
          <span className="ml-1 text-[11px] text-gray-400">Currency locks once you add a band.</span>
        </div>
        <TimezonePicker
          baseZone={configuration.baseTimezone}
          selectedZones={selectedZones}
          onChange={setSelectedZones}
        />
      </header>

      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-lg border border-ms-border bg-ms-surface p-4">
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResetClick}
              disabled={resetDisabled}
              title={resetTitle}
            >
              Reset
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleApplyPresetClick}
              disabled={presetDisabled}
              title={presetTitle}
            >
              Apply preset (Mon–Fri 08:00–18:00)
            </Button>
          </div>

          <div className="flex gap-6">
            <div className="flex-1">
              <PricingGrid
                cells={paintedCells}
                bands={bands}
                armedBandId={armedBandId}
                isErasing={isErasing}
                currency={configuration.currency}
                baseZone={configuration.baseTimezone}
                selectedZones={selectedZones}
                onPaintRange={handlePaintRange}
              />
            </div>
            <aside className="w-72 flex flex-col gap-4 sticky top-4 self-start">
              <PriceSummaryCard
                monthlyTotal={monthlyTotal}
                currency={configuration.currency}
                breakdown={breakdown}
                hasPaintedCells={paintedCells.length > 0}
                invalidBands={invalidBands}
              />
              {/* MKS-346 — which deals this package was priced for. Read-only:
                  the LEAD's form owns the link set, so there is no add or
                  remove affordance here, only the answer to "who is this
                  priced for?". */}
              <section className="rounded-lg border border-ms-border bg-ms-surface p-3">
                <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                  Linked leads
                </h2>
                {configuration.leads.length === 0 ? (
                  <p className="text-[13px] text-gray-400">
                    Not linked to any lead yet. Link it from the lead&apos;s SLA pricing picker.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {configuration.leads.map((linkedLead) => (
                      <li key={linkedLead.id} className="truncate text-[13px] text-gray-700">
                        {linkedLead.name}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <BandPanel
                bands={bands}
                armedBandId={armedBandId}
                isErasing={isErasing}
                hoursPerBand={hoursPerBand}
                onArmBand={(bandId) => {
                  setArmedBandId(bandId);
                  setIsErasing(false);
                }}
                onToggleErase={() => setIsErasing((v) => !v)}
                onAddBand={handleAddBand}
                onUpdateRate={handleUpdateRate}
                onDeleteBand={handleDeleteBand}
              />
            </aside>
          </div>
        </div>
      </div>

      <div
        data-testid="sla-pricing-save-bar"
        className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-3 z-10"
      >
        {saveStatus && (
          <span
            data-testid="sla-pricing-save-status"
            role={saveStatus.kind === 'error' ? 'alert' : 'status'}
            aria-live={saveStatus.kind === 'success' ? 'polite' : undefined}
            className={`mr-auto min-w-0 text-[13px] leading-snug ${
              saveStatus.kind === 'error' ? 'text-red-700' : 'text-green-700'
            }`}
          >
            {saveStatus.message}
          </span>
        )}
        {isDirty && <span className="text-sm text-gray-500">Unsaved changes</span>}
        <Button onClick={handleSave} isLoading={saveCells.isPending}>
          Save
        </Button>
      </div>

      <ResetGridConfirmModal
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={confirmReset}
        configurationName={configuration.name}
      />

      <ApplyPresetConfirmModal
        isOpen={isPresetModalOpen}
        onClose={() => setIsPresetModalOpen(false)}
        onConfirm={confirmApplyPreset}
        armedBandName={bands.find((b) => b.id === armedBandId)?.name ?? ''}
      />
    </div>
  );
}
